import {
  FormatListBulleted as FormatListBulletedIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Flag as FlagIcon,
  Textsms as TextsmsIcon,
  Settings as SettingsIcon,
  Dashboard as DashboardIcon,
  Logout as LogoutIcon,
  Groups as GroupsIcon,
  AutoAwesome as AutoAwesomeIcon,
  Diversity3 as Diversity3Icon,
} from '@mui/icons-material'
import type { ListItemIconProps } from '@mui/material'
import {
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ScopedCssBaseline,
  Stack,
} from '@mui/material'
import { styled as muiStyled } from '@mui/material/styles'
import type React from 'react'
import { useState, useEffect, useMemo } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import styled from 'styled-components'

import { useWindowSize } from '@/hooks/hooks'
import useUser from '@/hooks/useUser'
import type { Role } from '@/types/User'
import { hasRole } from '@/utils/role'
import { Colours } from '@/utils/styles'
import { allowedRoles } from '@/views/Executives'

interface Props {
  userData: any
  collapsedMenu: boolean
  setCollapsedMenu: Function
  handleLogOut: () => void
}

const RUDIText = styled.h1`
  color: ${Colours.darkTeal};
  text-align: left;
  padding: 0;
  margin: 0;
`

export const getNavigationDrawerWidth = (isOpen: boolean): string =>
  isOpen ? '200px' : '55px'

interface MenuElement {
  label: string
  path: string
  icon: JSX.Element
}

interface GetMenuItemArgs {
  personId: string
  roles: Role[]
}

const getMenuItems = ({ personId, roles }: GetMenuItemArgs): MenuElement[] => [
  {
    label: 'Next reference',
    path: 'next-applicant',
    icon: <AutoAwesomeIcon />,
  },
  {
    label: 'Applicants',
    path: 'applicants',
    icon: <FormatListBulletedIcon />,
  },
  {
    label: 'Fraud',
    path: 'fraud',
    icon: <FlagIcon />,
  },
  {
    label: 'My Dashboard',
    path: `executive/${personId}`,
    icon: <DashboardIcon />,
  },
  ...(hasRole(roles, allowedRoles)
    ? [
        {
          label: 'Executives',
          path: `executives`,
          icon: <GroupsIcon />,
        },
      ]
    : []),
  ...(hasRole(roles, allowedRoles)
    ? [
        {
          label: 'Squads',
          path: `squads`,
          icon: <Diversity3Icon />,
        },
      ]
    : []),
]

const DrawerHeader = styled.div`
  padding: 15px;
  height: 120px;
`

const LogoContainer = styled.img`
  height: auto;
  width: 100%;
`

const StyledListItemIcon = muiStyled(ListItemIcon)<ListItemIconProps>(() => ({
  minWidth: 0,
  marginTop: 4,
  marginBottom: 4,
}))

const StyledNavigationListItemIcon = muiStyled(StyledListItemIcon)<
  ListItemIconProps & { $isCollapsed: boolean }
>(({ $isCollapsed }) => ({
  minWidth: $isCollapsed ? 0 : 56,
}))

const feedbackUrl =
  'https://docs.google.com/forms/d/1b8AXbLUVNH28qrMAtck19RBKUlzIHGGfsExdb1t4JZQ/viewform?ts=652ffff7&edit_requested=true'

const MenuBar: React.FC<Props> = ({
  userData,
  collapsedMenu,
  setCollapsedMenu,
  handleLogOut,
}) => {
  const { personId, roles } = useUser()

  const [username, setUsername] = useState('')
  const location = useLocation()
  const MOBILE_BREAKPOINT = useMemo(() => {
    return 1400
  }, [])
  const size = useWindowSize()

  const [currPage, setCurrPage] = useState(
    window.location.pathname.replace('/', '').toLowerCase(),
  )

  useEffect(() => {
    if (!userData || !userData.name) return

    const { firstname, lastname } = userData.name

    if (firstname.trim() !== '') {
      setUsername(`${firstname} ${lastname}`)
    }
  }, [userData])

  useEffect(() => {
    setCurrPage(location.pathname.replace('/', ''))
  }, [location])

  useEffect(() => {
    if (size.width < MOBILE_BREAKPOINT) {
      setCollapsedMenu(true)
    } else {
      setCollapsedMenu(false)
    }
  }, [size.width]) // eslint-disable-line react-hooks/exhaustive-deps

  function avatarInitials() {
    if (username.trim() === '') {
      return ''
    }
    return `${username.split(' ')[0][0]}${username.split(' ')[1][0]}`.toUpperCase()
  }

  const getMenuLinks = useMemo(() => {
    return getMenuItems({ personId, roles })
  }, [personId, roles])

  const drawerWidth = collapsedMenu ? 55 : 200

  return (
    <ScopedCssBaseline>
      <Drawer
        variant="permanent"
        anchor="left"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        <DrawerHeader>
          {collapsedMenu ? (
            <LogoContainer src="/assets/goodlord-logo-square.png" />
          ) : (
            <>
              <LogoContainer src="/assets/goodlord-logo.png" /> <RUDIText>RUDI</RUDIText>
            </>
          )}
        </DrawerHeader>
        <Divider />
        {collapsedMenu ? (
          <List disablePadding>
            <ListItem disablePadding>
              <ListItemButton onClick={() => setCollapsedMenu(false)}>
                <StyledListItemIcon>
                  <ChevronRightIcon />
                </StyledListItemIcon>
              </ListItemButton>
            </ListItem>
          </List>
        ) : (
          <Stack direction="row" justifyContent="flex-end">
            <Divider orientation="vertical" flexItem />
            <List disablePadding>
              <ListItem disablePadding>
                <ListItemButton onClick={() => setCollapsedMenu(true)}>
                  <StyledListItemIcon>
                    <ChevronLeftIcon />
                  </StyledListItemIcon>
                </ListItemButton>
              </ListItem>
            </List>
          </Stack>
        )}
        <Divider />
        <List disablePadding>
          {getMenuLinks.map((menuItem: MenuElement, index: number) => (
            <ListItem key={index} disablePadding>
              <ListItemButton
                component={RouterLink}
                to={`/${menuItem.path}`}
                selected={currPage.toLowerCase() === menuItem.path}
              >
                <StyledNavigationListItemIcon $isCollapsed={collapsedMenu}>
                  {menuItem.icon}
                </StyledNavigationListItemIcon>
                {!collapsedMenu && <ListItemText primary={menuItem.label} />}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Divider />
        <List disablePadding>
          <ListItem disablePadding>
            <ListItemButton href={feedbackUrl} target="_blank">
              <StyledNavigationListItemIcon $isCollapsed={collapsedMenu}>
                <TextsmsIcon />
              </StyledNavigationListItemIcon>
              {!collapsedMenu && <ListItemText primary="Feedback" />}
            </ListItemButton>
          </ListItem>
          {avatarInitials() !== '' && (
            <ListItem disablePadding>
              <ListItemButton
                component={RouterLink}
                to="/profile"
                selected={currPage.toLowerCase() === 'profile'}
              >
                <StyledNavigationListItemIcon $isCollapsed={collapsedMenu}>
                  <SettingsIcon />
                </StyledNavigationListItemIcon>
                {!collapsedMenu && <ListItemText primary="Settings" />}
              </ListItemButton>
            </ListItem>
          )}
          <ListItem disablePadding>
            <ListItemButton onClick={handleLogOut}>
              <StyledNavigationListItemIcon $isCollapsed={collapsedMenu}>
                <LogoutIcon />
              </StyledNavigationListItemIcon>
              {!collapsedMenu && <ListItemText primary="Logout" />}
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
    </ScopedCssBaseline>
  )
}

export default MenuBar
